import type { ColumnGroup } from "ag-grid-community";
import { marked } from "marked";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getRunnableSelectedCellIds } from "../../hooks/useRunSelectedCells";
import { normalizeLLMAssertionValue } from "../assertions";
import { useSmartTableCellActionContext } from "../cell-action-context";
import { getAnchorCellLeadingIcon } from "../CellPreview/anchorCellLeadingIcon";
import type {
  CellExecutionMetricsOverlay,
  CellRendererModel,
  CellRendererParams,
  InlineClassifiedContent,
  StatusFilterHighlightTone,
} from "../types";
import {
  classifyTextValue,
  extractCompareDiff,
  MEDIA_TYPES,
  shouldRenderAsRichText,
} from "../utils";

import {
  HISTORY_IMPORT_COLUMN_CONFIG,
  HistoryImportSource,
} from "@/routes/smart-table/utils/historyImportTypes";
import { parseAnchorCellValue } from "@/routes/smart-table/utils/parseAnchorCellValue";
import { fetchSmartTableCell, getSmartTableCellQueryKey } from "@/queries/smart-tables";
import {
  CellStatus,
  ColumnType,
  isTruncatedValueStub,
  PENDING_CELL_STATUSES,
  type JsonValue,
  type SmartCell,
  type SmartColumn,
} from "@/queries/smart-tables/types";
import { useSheetDataStoreSafe } from "@/routes/smart-table/sheet-data-store-context";
import type { CellSnapshot } from "@/routes/smart-table/stores/sheet-data-store";
import {
  DEFAULT_CELL_ERROR_MESSAGE,
  getUserFacingCellError,
  resolveCellDisplayState,
} from "@/routes/smart-table/utils/cellDisplayState";
import {
  isExecutionMetadataMetricKey,
  isPlainTextEditableColumnType,
  isReadonlyAnchorColumnType,
  isRequestColumnType,
  isRunnableColumnType,
  isStableDisplayColumnType,
  isTextColumnType,
  isTraceColumnType,
} from "@/routes/smart-table/utils/columnTypeGuards";
import {
  getHumanInputDisplayValue,
  resolveHumanInputConfiguration,
  unwrapHumanCellValue,
} from "@/routes/smart-table/utils/humanInput";
import { isExecutionMetadataChildColumn } from "@/routes/smart-table/utils/smartTableGroupedColumns";
import {
  CellContentType,
  classifyContent,
  getCopyTextForClassifiedContent,
  looksLikeMarkdown,
  normalizeForClassification,
  parseMarkdownCodeFence,
  stripMarkdownForPreview,
} from "@/routes/smart-table/utils/contentType";
import {
  formatExecutionMetadataLatency,
  formatExecutionMetadataPrice,
} from "@/routes/smart-table/utils/executionMetadataFormatting";
import { parseSourceResolver } from "@/routes/smart-table/utils/sourceResolver";
import {
  isMultiContentValue,
  isOutputMediaItem,
  isOutputMediaItemArray,
  type MultiContentItem,
  type MultiContentValue,
  type OutputMediaItem,
} from "@/routes/smart-table/utils/multiContent";
import { displayErrorToast, displayToast } from "@/utils/toast";
import { ToastType } from "@/enums/toast";

const MAX_INLINE_RICH_TEXT_LENGTH = 280;
const MAX_INLINE_CODE_FENCE_RICH_TEXT_LENGTH = 4_096;
const CELL_RENDER_CACHE_LIMIT = 1_000;
const MAX_CACHEABLE_TEXT_LENGTH = 4_096;
const MAX_TOOLTIP_STRIP_LENGTH = 4_096;

const getAnchorImportType = (columnType: ColumnType | undefined): HistoryImportSource | null => {
  if (isRequestColumnType(columnType)) {
    return HistoryImportSource.Request;
  }
  return isTraceColumnType(columnType) ? HistoryImportSource.Trace : null;
};

const getAnchorImportLabel = (type: HistoryImportSource | null): string | undefined =>
  type ? HISTORY_IMPORT_COLUMN_CONFIG[type].anchorImportLabel : undefined;

export const getComputedBooleanValue = ({
  displayText,
  isComputedDisplay,
  rawStoredValue,
  richHtml,
}: {
  displayText: string;
  isComputedDisplay: boolean;
  rawStoredValue: unknown;
  richHtml: string | null;
}): boolean | null => {
  if (!isComputedDisplay || richHtml) {
    return null;
  }

  if (typeof rawStoredValue === "boolean") {
    return rawStoredValue;
  }

  if (
    rawStoredValue &&
    typeof rawStoredValue === "object" &&
    !Array.isArray(rawStoredValue) &&
    "value" in rawStoredValue &&
    typeof rawStoredValue.value === "boolean"
  ) {
    return rawStoredValue.value;
  }

  const normalizedDisplayText = displayText.trim().toLowerCase();
  if (normalizedDisplayText === "true") {
    return true;
  }
  if (normalizedDisplayText === "false") {
    return false;
  }

  return null;
};

export const shouldInheritParentPendingLikeStatus = ({
  isTextLikeColumn,
  isExecutionMetricChild = false,
  parentColumnId,
  rawStatus,
}: {
  isTextLikeColumn: boolean;
  isExecutionMetricChild?: boolean;
  parentColumnId?: string;
  rawStatus: CellStatus;
}): boolean =>
  isTextLikeColumn &&
  parentColumnId !== undefined &&
  (isExecutionMetricChild ||
    PENDING_CELL_STATUSES.has(rawStatus) ||
    rawStatus === CellStatus.STALE);

const getStatusFilterHighlightTone = (
  status: CellStatus,
  selectedStatuses: readonly CellStatus[],
): StatusFilterHighlightTone => {
  if (!selectedStatuses.includes(status)) {
    return null;
  }

  if (PENDING_CELL_STATUSES.has(status)) {
    return "active";
  }

  if (status === CellStatus.FAILED) {
    return "failed";
  }

  if (status === CellStatus.COMPLETED) {
    return "completed";
  }

  return null;
};

const richHtmlCache = new Map<string, string | null>();
const tooltipTextCache = new Map<string, string>();

const canCacheByTextSize = (text: string): boolean => text.length <= MAX_CACHEABLE_TEXT_LENGTH;

const getTooltipCacheKey = (text: string): string => text.slice(0, MAX_TOOLTIP_STRIP_LENGTH);

const rememberCacheValue = <T>(cache: Map<string, T>, key: string, value: T): T => {
  if (!cache.has(key) && cache.size >= CELL_RENDER_CACHE_LIMIT) {
    const oldestKey = cache.keys().next().value;
    if (oldestKey !== undefined) {
      cache.delete(oldestKey);
    }
  }

  cache.set(key, value);
  return value;
};

const getRichHtml = (text: string): string | null => {
  const shouldCache = canCacheByTextSize(text);

  if (shouldCache && richHtmlCache.has(text)) {
    return richHtmlCache.get(text) ?? null;
  }

  try {
    const parsed = marked.parse(text, { gfm: true, breaks: true }) as string;
    return shouldCache ? rememberCacheValue(richHtmlCache, text, parsed) : parsed;
  } catch {
    return shouldCache ? rememberCacheValue(richHtmlCache, text, null) : null;
  }
};

export const getPlainTooltipText = (text: string): string => {
  const cacheKey = getTooltipCacheKey(text);
  const cached = tooltipTextCache.get(cacheKey);
  if (cached !== undefined) {
    return cached;
  }

  const tooltipText = stripMarkdownForPreview(text.slice(0, MAX_TOOLTIP_STRIP_LENGTH));
  return rememberCacheValue(tooltipTextCache, cacheKey, tooltipText);
};

const readFileAsDataUrl = async (file: File): Promise<string> =>
  await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
    reader.readAsDataURL(file);
  });

const fileToOutputMediaItem = async (file: File): Promise<OutputMediaItem> => ({
  type: "output_media",
  media_type: file.type || undefined,
  url: await readFileAsDataUrl(file),
  title: file.name,
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isExecutionMetricColumn = (
  column: SmartColumn,
  parentColumnId: string,
  metricKey: "price" | "latency_ms",
): boolean => {
  if (column.metric_key !== metricKey) {
    return false;
  }

  if (column.group_parent_column_id === parentColumnId) {
    return true;
  }

  const config = isRecord(column.config) ? column.config : null;
  return config?.source_column_id === parentColumnId && config?.metric_key === metricKey;
};

const getExecutionMetricColumn = (
  columns: ReadonlyMap<string, SmartColumn>,
  parentColumn: SmartColumn,
  metricKey: "price" | "latency_ms",
): SmartColumn | null => {
  const explicitChildIds = Array.isArray(parentColumn.group_child_column_ids)
    ? parentColumn.group_child_column_ids
    : [];

  for (const childId of explicitChildIds) {
    if (typeof childId !== "string") {
      continue;
    }

    const childColumn = columns.get(childId);
    if (childColumn && isExecutionMetricColumn(childColumn, parentColumn.id, metricKey)) {
      return childColumn;
    }
  }

  for (const childColumn of columns.values()) {
    if (isExecutionMetricColumn(childColumn, parentColumn.id, metricKey)) {
      return childColumn;
    }
  }

  return null;
};

const getRowMetricValue = (data: unknown, columnId: string): unknown => {
  if (!isRecord(data)) {
    return undefined;
  }

  return data[`_value_${columnId}`] ?? data[columnId];
};

const getAncestorColumnGroupById = (
  column: CellRendererParams["column"] | null | undefined,
  groupId: string | undefined,
): ColumnGroup | null => {
  if (!column || !groupId) {
    return null;
  }

  let parentGroup = column.getParent();
  while (parentGroup) {
    if (parentGroup.getGroupId() === groupId) {
      return parentGroup;
    }

    parentGroup = parentGroup.getParent();
  }

  return null;
};

const toNonEmptyMetricText = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const isServerBackedCellId = (cellId: string): boolean => !cellId.startsWith("temp-");

const isJsonValueLike = (value: unknown): value is JsonValue => {
  if (value === null) {
    return true;
  }

  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return true;
  }

  if (Array.isArray(value)) {
    return value.every(isJsonValueLike);
  }

  if (value !== null && typeof value === "object") {
    return Object.values(value as Record<string, unknown>).every(isJsonValueLike);
  }

  return false;
};

interface CompositionSourceRef {
  source_resolver?: string;
  source_cell_id?: string;
  source_sheet_id?: string;
}

interface RowCancelTarget {
  tableId: string;
  sheetId: string;
}

const getCompositionRefTarget = (
  sourceRef: CompositionSourceRef,
  fallbackTableId: string,
): RowCancelTarget | null => {
  const sourceSheetId = sourceRef.source_sheet_id;
  if (!sourceSheetId) {
    return null;
  }

  const parsedSourceResolver = sourceRef.source_resolver
    ? parseSourceResolver(sourceRef.source_resolver)
    : null;

  return {
    tableId: parsedSourceResolver?.smartTableId ?? fallbackTableId,
    sheetId: sourceSheetId,
  };
};

const resolveCompositionRowCancelTargets = ({
  backingCell,
  compositionSource,
  tableId,
}: {
  backingCell: unknown;
  compositionSource?: string;
  tableId?: string;
}): RowCancelTarget[] => {
  if (!tableId || !backingCell || !isRecord(backingCell)) {
    return [];
  }

  if (Array.isArray(backingCell.source_refs)) {
    const sourceRefs = backingCell.source_refs.filter(isRecord) as CompositionSourceRef[];
    const matchingRefs = compositionSource
      ? sourceRefs.filter(sourceRef => sourceRef.source_resolver === compositionSource)
      : sourceRefs;

    const targets = matchingRefs
      .map(sourceRef => getCompositionRefTarget(sourceRef, tableId))
      .filter((target): target is RowCancelTarget => target !== null);

    const seen = new Set<string>();
    return targets.filter(target => {
      const key = `${target.tableId}:${target.sheetId}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  const sourceSheetId =
    typeof backingCell.source_sheet_id === "string" ? backingCell.source_sheet_id : null;
  if (!sourceSheetId) {
    return [];
  }

  const parsedSourceResolver = compositionSource ? parseSourceResolver(compositionSource) : null;
  return [
    {
      tableId: parsedSourceResolver?.smartTableId ?? tableId,
      sheetId: sourceSheetId,
    },
  ];
};

const getMediaItemsFromClassifiedContent = (
  classified: InlineClassifiedContent | ReturnType<typeof classifyContent>,
) => {
  if (classified.type === CellContentType.MULTI_CONTENT && classified.multiContentPayload) {
    const mediaItems = classified.multiContentPayload.items.filter(isOutputMediaItem);
    return mediaItems.length > 0 ? mediaItems : undefined;
  }

  return classified.mediaPayload;
};

const INLINE_MEDIA_MARKER_SCAN_LENGTH = 4_096;
type SmartCellDisplayType = NonNullable<NonNullable<SmartCell["metadata"]>["display_type"]>;
const SMART_CELL_DISPLAY_TYPES = new Set<SmartCellDisplayType>([
  "boolean",
  "number",
  "object",
  "string",
  "unknown",
  "html",
  "markdown",
]);

const getJsonStringField = (text: string, field: string): string | undefined => {
  const match = new RegExp(`"${field}"\\s*:\\s*("(?:\\\\.|[^"\\\\])*")`).exec(text);
  if (!match?.[1]) {
    return undefined;
  }

  try {
    return JSON.parse(match[1]) as string;
  } catch {
    return undefined;
  }
};

export const resolveInlineStructuredMedia = (
  value: unknown,
  displayValue?: unknown,
): unknown | null => {
  const candidate = isRecord(value) && "value" in value ? value.value : value;
  if (
    isOutputMediaItem(candidate) ||
    isOutputMediaItemArray(candidate) ||
    isMultiContentValue(candidate)
  ) {
    return candidate;
  }

  if (typeof displayValue !== "string") {
    return null;
  }

  const preview = displayValue.slice(0, INLINE_MEDIA_MARKER_SCAN_LENGTH);
  if (getJsonStringField(preview, "type") !== "output_media") {
    return null;
  }

  return {
    type: "output_media",
    media_type: getJsonStringField(preview, "media_type"),
    storage_key: getJsonStringField(preview, "storage_key"),
    title: getJsonStringField(preview, "title"),
    url: getJsonStringField(preview, "url"),
  };
};

export const classifyInlineCellValue = ({
  rawStoredValue,
  cellValue,
  classificationValue,
  isTextLikeColumn,
  cellDisplayType,
}: {
  rawStoredValue: unknown;
  cellValue: unknown;
  classificationValue: unknown;
  isTextLikeColumn: boolean;
  cellDisplayType?: SmartCellDisplayType;
}): InlineClassifiedContent | ReturnType<typeof classifyContent> => {
  const structuredMedia = resolveInlineStructuredMedia(rawStoredValue, cellValue);
  if (structuredMedia) {
    return classifyContent(structuredMedia, typeof cellValue === "string" ? cellValue : undefined);
  }

  if (cellDisplayType !== undefined) {
    return classifyTextValue(cellValue, cellDisplayType);
  }

  return isTextLikeColumn ? classifyTextValue(cellValue) : classifyContent(classificationValue);
};

export const getSmartTableCellCopyText = (
  rawValue: unknown,
  displayValue: string | null | undefined,
  { preferRawValue = false }: { preferRawValue?: boolean } = {},
): string => {
  const classificationValue = preferRawValue
    ? rawValue
    : normalizeForClassification(rawValue, displayValue);
  return getCopyTextForClassifiedContent(
    classifyContent(classificationValue, preferRawValue ? undefined : displayValue),
  );
};

export const applyStatusErrorClassification = ({
  classified,
  classificationValue,
  errorMessage,
  isTextLikeColumn,
  status,
}: {
  classified: InlineClassifiedContent | ReturnType<typeof classifyContent>;
  classificationValue: unknown;
  errorMessage?: string;
  isTextLikeColumn: boolean;
  status: CellStatus;
}): InlineClassifiedContent | ReturnType<typeof classifyContent> => {
  if (isTextLikeColumn) {
    return classified;
  }

  if (status !== CellStatus.FAILED && !(status === CellStatus.STALE && Boolean(errorMessage))) {
    return classified;
  }

  const displayPreview = errorMessage ?? DEFAULT_CELL_ERROR_MESSAGE;
  return {
    type: CellContentType.ERROR,
    displayPreview,
    rawValue: classificationValue,
    parsedValue: classificationValue,
    isStructuredType: true,
  };
};

const mergeUploadedMediaIntoCellValue = (
  currentValue: unknown,
  newMedia: OutputMediaItem[],
): MultiContentValue | OutputMediaItem[] => {
  if (newMedia.length === 0) {
    if (isMultiContentValue(currentValue)) {
      return currentValue;
    }
    if (isOutputMediaItemArray(currentValue)) {
      return currentValue;
    }
    return [];
  }

  if (isMultiContentValue(currentValue)) {
    return {
      type: "multi_content",
      items: [...currentValue.items, ...newMedia],
    };
  }

  if (isOutputMediaItemArray(currentValue)) {
    return [...currentValue, ...newMedia];
  }

  if (isOutputMediaItem(currentValue)) {
    return [currentValue, ...newMedia];
  }

  const normalized = normalizeForClassification(currentValue, null);

  if (typeof normalized === "string" && normalized.trim().length > 0) {
    const items: MultiContentItem[] = [{ type: "text", text: normalized }, ...newMedia];
    return { type: "multi_content", items };
  }

  if (normalized !== null && normalized !== undefined && normalized !== "") {
    if (isJsonValueLike(normalized)) {
      const items: MultiContentItem[] = [{ type: "json", value: normalized }, ...newMedia];
      return { type: "multi_content", items };
    }

    const items: MultiContentItem[] = [{ type: "text", text: String(normalized) }, ...newMedia];
    return { type: "multi_content", items };
  }

  return [...newMedia];
};

export default function useCellRendererModel(params: CellRendererParams): CellRendererModel {
  const {
    data,
    colDef,
    eGridCell,
    api,
    valueFormatted,
    value,
    column,
    columnType,
    parentColumnId,
    compositionSource,
    metricKey,
    openDialog,
    tableId,
    sheetId,
    globalSearchQuery = "",
    selectedCellStatuses = [],
    canEditCell,
    isReadOnly = false,
  } = params;
  const cellActionContext = useSmartTableCellActionContext();
  const sheetDataStore = useSheetDataStoreSafe();
  const updateCellMutationRef = cellActionContext?.updateCellMutationRef;
  const recalculateCellMutationRef = cellActionContext?.recalculateCellMutationRef;
  const batchRecalculateCellsMutationRef = cellActionContext?.batchRecalculateCellsMutationRef;
  const recalculateColumnMutationRef = cellActionContext?.recalculateColumnMutationRef;
  const cancelCellMutationRef = cellActionContext?.cancelCellMutationRef;
  const cancelRowsMutationRef = cellActionContext?.cancelRowsMutationRef;
  const queryClient = cellActionContext?.queryClient;
  const wid = cellActionContext?.wid;
  const workspaceId = cellActionContext?.workspaceId;
  const userToken = cellActionContext?.userToken ?? "";
  const openAnchorCellImport = cellActionContext?.openAnchorCellImport;

  const preRunCompositionSnapshotsRef = useRef<Map<string, CellSnapshot>>(new Map());

  const rowIndex = data?._rowIndex as number | undefined;
  const rawColumnId = (colDef?.colId ?? colDef?.field) as string | undefined;
  const baseColumnId = parentColumnId ?? rawColumnId?.split("_")[0];
  const statusColumnId = baseColumnId;
  const dialogColumnId = rawColumnId;
  // Read live from the store on every render. Do not memoize on store identity —
  // after optimistic row create, reconcile replaces temp cells in-place and AG Grid
  // refreshCells reuses this renderer instance without changing rowIndex/columnId.
  const backingCell =
    sheetDataStore && rowIndex !== undefined && baseColumnId !== undefined
      ? (sheetDataStore.rows.get(rowIndex)?.get(baseColumnId) ?? null)
      : null;
  const rowCellId =
    data && baseColumnId !== undefined && typeof data[`_cellId_${baseColumnId}`] === "string"
      ? data[`_cellId_${baseColumnId}`]
      : undefined;
  const rowCellMetadata =
    data && baseColumnId !== undefined ? data[`_metadata_${baseColumnId}`] : undefined;
  const rowDisplayType =
    isRecord(rowCellMetadata) &&
    typeof rowCellMetadata.display_type === "string" &&
    SMART_CELL_DISPLAY_TYPES.has(rowCellMetadata.display_type as SmartCellDisplayType)
      ? (rowCellMetadata.display_type as SmartCellDisplayType)
      : undefined;
  const cellDisplayType = backingCell?.metadata?.display_type ?? rowDisplayType;
  const executionMetricColumnGroup = useMemo(
    () => getAncestorColumnGroupById(column, rawColumnId),
    [column, rawColumnId],
  );
  const [isExecutionMetricGroupCollapsed, setIsExecutionMetricGroupCollapsed] = useState(() =>
    executionMetricColumnGroup ? !executionMetricColumnGroup.isExpanded() : false,
  );
  const isGroupLeafColumn = parentColumnId !== undefined;
  const isCompositionSubColumn = isGroupLeafColumn && compositionSource !== undefined;
  const isExecutionMetricChild = isGroupLeafColumn && isExecutionMetadataMetricKey(metricKey);
  const isTextLikeColumn = isTextColumnType(columnType) || isReadonlyAnchorColumnType(columnType);
  const isHumanColumn = columnType === ColumnType.HUMAN;
  const isStableDisplayColumn = isStableDisplayColumnType(columnType);
  const isComputedColumn = !isStableDisplayColumn;
  const cellValue = valueFormatted ?? value;

  const isHydrating =
    data?._pendingHydration === true ||
    (statusColumnId !== undefined && data?.[`_pendingHydration_${statusColumnId}`] === true);

  const rawStatus =
    data && statusColumnId !== undefined
      ? ((data[`_status_${statusColumnId}`] as CellStatus | undefined) ?? CellStatus.STALE)
      : CellStatus.STALE;

  const shouldTextColumnInheritPendingLikeStatus = shouldInheritParentPendingLikeStatus({
    isTextLikeColumn,
    isExecutionMetricChild,
    parentColumnId,
    rawStatus,
  });

  let status: CellStatus =
    isStableDisplayColumn && !shouldTextColumnInheritPendingLikeStatus
      ? CellStatus.COMPLETED
      : rawStatus;

  let errorMessage: string | undefined =
    isComputedColumn && data && statusColumnId !== undefined
      ? (data[`_error_${statusColumnId}`] as string | undefined)
      : undefined;

  const rawStoredValue =
    data && statusColumnId !== undefined ? data[`_value_${statusColumnId}`] : undefined;

  const displayState = useMemo(
    () =>
      isComputedColumn
        ? resolveCellDisplayState({
            status,
            display_value: cellValue,
            value: isExecutionMetricChild ? value : (rawStoredValue ?? value),
          })
        : null,
    [isComputedColumn, status, cellValue, isExecutionMetricChild, rawStoredValue, value],
  );

  if (
    isComputedColumn &&
    displayState?.hasErrorValue &&
    status !== CellStatus.STALE &&
    !PENDING_CELL_STATUSES.has(status)
  ) {
    status = CellStatus.FAILED;
  }

  errorMessage = isComputedColumn ? (errorMessage ?? displayState?.errorMessage) : undefined;

  const classificationValue =
    displayState?.isErrorState || displayState?.hasErrorValue
      ? displayState.valueToDisplay
      : cellValue;

  const parsedAnchorValue = useMemo(() => parseAnchorCellValue(rawStoredValue), [rawStoredValue]);

  const promptRequestId = useMemo(() => {
    const isExecutionRootLeaf =
      parentColumnId === undefined &&
      (columnType === ColumnType.PROMPT_TEMPLATE || columnType === ColumnType.WORKFLOW);

    return isExecutionRootLeaf ? parsedAnchorValue.requestId : null;
  }, [columnType, parentColumnId, parsedAnchorValue.requestId]);

  const isLLMAssertion = columnType === ColumnType.LLM_ASSERTION;
  const isCompare = columnType === ColumnType.COMPARE;
  const compareDiff = useMemo(
    () => (isCompare ? extractCompareDiff(rawStoredValue) : null),
    [isCompare, rawStoredValue],
  );
  const assertionModel = useMemo(
    () =>
      isLLMAssertion ? normalizeLLMAssertionValue(classificationValue, rawStoredValue) : undefined,
    [isLLMAssertion, classificationValue, rawStoredValue],
  );

  const contentClassification: InlineClassifiedContent | ReturnType<typeof classifyContent> =
    useMemo(
      () =>
        classifyInlineCellValue({
          rawStoredValue,
          cellValue,
          classificationValue,
          isTextLikeColumn,
          cellDisplayType,
        }),
      [isTextLikeColumn, classificationValue, rawStoredValue, cellValue, cellDisplayType],
    );

  const classified: InlineClassifiedContent | ReturnType<typeof classifyContent> = useMemo(
    () =>
      applyStatusErrorClassification({
        classified: contentClassification,
        classificationValue,
        errorMessage,
        isTextLikeColumn,
        status,
      }),
    [contentClassification, classificationValue, errorMessage, isTextLikeColumn, status],
  );

  const { displayPreview: displayText } = classified;
  const isJson = classified.type === "json";
  const linkPayload = classified.type === CellContentType.LINK ? classified.linkPayload : undefined;
  const mediaPayload = useMemo(() => getMediaItemsFromClassifiedContent(classified), [classified]);
  const isMedia = MEDIA_TYPES.has(classified.type) && mediaPayload !== undefined;
  const isPending = isHydrating || PENDING_CELL_STATUSES.has(status);
  const hasContent = displayText.trim().length > 0;
  const isVisuallyStale =
    !isHydrating &&
    (status === CellStatus.STALE || (isCompositionSubColumn && rawStatus === CellStatus.STALE));
  const isError =
    isComputedColumn &&
    (status === CellStatus.FAILED || (status === CellStatus.STALE && Boolean(errorMessage)));
  const statusFilterHighlightTone =
    isComputedColumn || shouldTextColumnInheritPendingLikeStatus
      ? getStatusFilterHighlightTone(status, selectedCellStatuses)
      : null;
  const isStandaloneCodeFence = useMemo(
    () =>
      displayText.length <= MAX_INLINE_CODE_FENCE_RICH_TEXT_LENGTH &&
      Boolean(parseMarkdownCodeFence(displayText)),
    [displayText],
  );
  const shouldTryMarkdownRender =
    (displayText.length <= MAX_INLINE_RICH_TEXT_LENGTH ||
      (isStandaloneCodeFence && displayText.length <= MAX_INLINE_CODE_FENCE_RICH_TEXT_LENGTH)) &&
    (classified.type === "markdown" || looksLikeMarkdown(displayText));
  const useRichText =
    shouldRenderAsRichText(classified.type, isError) && hasContent && shouldTryMarkdownRender;

  const userFacingError = useMemo(
    () => (isError ? getUserFacingCellError(errorMessage ?? displayText) : null),
    [isError, errorMessage, displayText],
  );

  const tooltipText = useMemo(() => {
    if (userFacingError) {
      return userFacingError.message;
    }

    if (isJson) {
      return displayText.slice(0, MAX_TOOLTIP_STRIP_LENGTH);
    }

    return getPlainTooltipText(displayText);
  }, [userFacingError, isJson, displayText]);

  const richHtml = useMemo(() => {
    if (!useRichText) {
      return null;
    }

    return getRichHtml(displayText);
  }, [useRichText, displayText]);

  const textClass = useMemo(() => {
    if (isVisuallyStale) {
      return isError ? "text-red-500 opacity-60" : "opacity-60";
    }

    if (status === CellStatus.FAILED) {
      return "text-red-500";
    }

    return "";
  }, [isVisuallyStale, isError, status]);

  const isComputedDisplay =
    isComputedColumn && !isError && !isVisuallyStale && status !== CellStatus.FAILED;
  const smartComputedTextClass = isComputedDisplay ? "text-slate-500 dark:text-slate-400" : "";

  const computedBooleanValue = getComputedBooleanValue({
    displayText: tooltipText,
    isComputedDisplay,
    rawStoredValue,
    richHtml,
  });

  const onOpenDialog = useCallback(() => {
    if (rowIndex === undefined || dialogColumnId === undefined) {
      return;
    }

    openDialog?.(rowIndex, dialogColumnId, { focusEditor: true });
  }, [openDialog, rowIndex, dialogColumnId]);

  const canOpenDialog = !isHydrating && rowIndex !== undefined && dialogColumnId !== undefined;

  // Mocked prompt runs carry their whole trace on the cell — link to the trace, not the LLM call.
  const mockTraceId = parsedAnchorValue.traceId;

  const openRequestUrl = useMemo(() => {
    if (!workspaceId) {
      return undefined;
    }

    if (columnType === ColumnType.PROMPT_TEMPLATE) {
      if (mockTraceId !== null) {
        return `/workspace/${workspaceId}/traces/${mockTraceId}`;
      }
      return promptRequestId === null
        ? undefined
        : `/workspace/${workspaceId}/request/${promptRequestId}`;
    }

    if (columnType === ColumnType.WORKFLOW || isTraceColumnType(columnType)) {
      return mockTraceId === null ? undefined : `/workspace/${workspaceId}/traces/${mockTraceId}`;
    }

    if (isRequestColumnType(columnType)) {
      return parsedAnchorValue.requestId === null
        ? undefined
        : `/workspace/${workspaceId}/request/${parsedAnchorValue.requestId}`;
    }

    return undefined;
  }, [workspaceId, columnType, promptRequestId, mockTraceId, parsedAnchorValue.requestId]);

  const openRequestLabel = useMemo(() => {
    if (columnType === ColumnType.PROMPT_TEMPLATE) {
      return mockTraceId !== null ? "Open trace" : "Open request log";
    }

    if (columnType === ColumnType.WORKFLOW || isTraceColumnType(columnType)) {
      return "Open trace";
    }

    if (isRequestColumnType(columnType)) {
      return "Open request";
    }

    return undefined;
  }, [columnType, mockTraceId]);

  useEffect(() => {
    if (!executionMetricColumnGroup) {
      setIsExecutionMetricGroupCollapsed(false);
      return;
    }

    const providedGroup = executionMetricColumnGroup.getProvidedColumnGroup();
    const syncGroupCollapsedState = () => {
      setIsExecutionMetricGroupCollapsed(!providedGroup.isExpanded());
    };

    syncGroupCollapsedState();
    providedGroup.addEventListener("expandedChanged", syncGroupCollapsedState);

    return () => {
      providedGroup.removeEventListener("expandedChanged", syncGroupCollapsedState);
    };
  }, [executionMetricColumnGroup]);

  const backingColumn =
    baseColumnId !== undefined ? sheetDataStore?.columns.get(baseColumnId) : undefined;
  const isCompositionColumnCell = backingColumn?.type === ColumnType.COMPOSITION;

  const executionMetricsOverlay = useMemo<CellExecutionMetricsOverlay | null>(() => {
    const isExecutionRootLeaf =
      parentColumnId === undefined &&
      backingColumn !== undefined &&
      (backingColumn.type === ColumnType.PROMPT_TEMPLATE ||
        backingColumn.type === ColumnType.WORKFLOW ||
        isReadonlyAnchorColumnType(backingColumn.type));

    if (!isExecutionRootLeaf || !sheetDataStore || !isExecutionMetricGroupCollapsed) {
      return null;
    }

    const priceColumn = getExecutionMetricColumn(sheetDataStore.columns, backingColumn, "price");
    const latencyColumn = getExecutionMetricColumn(
      sheetDataStore.columns,
      backingColumn,
      "latency_ms",
    );

    const cost = priceColumn
      ? toNonEmptyMetricText(formatExecutionMetadataPrice(getRowMetricValue(data, priceColumn.id)))
      : null;
    const latency = latencyColumn
      ? toNonEmptyMetricText(
          formatExecutionMetadataLatency(getRowMetricValue(data, latencyColumn.id)),
        )
      : null;

    return cost || latency ? { cost, latency } : null;
  }, [backingColumn, data, isExecutionMetricGroupCollapsed, parentColumnId, sheetDataStore]);

  const resolvedCellId = backingCell?.id ?? rowCellId;
  const actionableCellId =
    typeof resolvedCellId === "string" && isServerBackedCellId(resolvedCellId)
      ? resolvedCellId
      : null;

  const compositionRowCancelTargets = useMemo(
    () =>
      isCompositionColumnCell
        ? resolveCompositionRowCancelTargets({
            backingCell,
            compositionSource,
            tableId,
          })
        : [],
    [backingCell, compositionSource, isCompositionColumnCell, tableId],
  );

  const hasCellActionContext =
    cellActionContext !== null &&
    queryClient !== undefined &&
    wid !== undefined &&
    Number.isFinite(wid);

  const isSystemManagedField = isExecutionMetadataChildColumn(backingColumn);
  const canUpload =
    !isReadOnly &&
    !isHydrating &&
    !isSystemManagedField &&
    updateCellMutationRef !== undefined &&
    rowIndex !== undefined &&
    baseColumnId !== undefined &&
    isPlainTextEditableColumnType(columnType) &&
    Boolean(canEditCell?.(rowIndex, baseColumnId)) &&
    Boolean(tableId) &&
    Boolean(sheetId);

  const humanInputConfig = useMemo(
    () =>
      isHumanColumn
        ? resolveHumanInputConfiguration(
            (backingColumn?.config as Record<string, unknown> | null | undefined) ?? null,
          )
        : null,
    [backingColumn?.config, isHumanColumn],
  );

  const humanInputValue = useMemo(
    () => (isHumanColumn ? unwrapHumanCellValue(rawStoredValue ?? cellValue) : null),
    [cellValue, isHumanColumn, rawStoredValue],
  );

  const canEditHumanInput =
    !isReadOnly &&
    !isHydrating &&
    isHumanColumn &&
    updateCellMutationRef !== undefined &&
    actionableCellId !== null &&
    rowIndex !== undefined &&
    baseColumnId !== undefined &&
    Boolean(canEditCell?.(rowIndex, baseColumnId)) &&
    Boolean(tableId) &&
    Boolean(sheetId);

  const onSaveHumanInput = useCallback(
    async (nextValue: string | number): Promise<void> => {
      if (
        !canEditHumanInput ||
        !sheetDataStore ||
        !updateCellMutationRef ||
        !tableId ||
        !sheetId ||
        !actionableCellId ||
        !humanInputConfig
      ) {
        return;
      }

      const nextDisplayValue = getHumanInputDisplayValue(
        humanInputConfig.ui_element?.component,
        nextValue,
        humanInputConfig,
      );
      const nextJsonValue = { value: nextValue };
      const snapshot = sheetDataStore.getCellSnapshot(actionableCellId);
      sheetDataStore.updateCell(actionableCellId, nextDisplayValue, nextJsonValue);

      try {
        const serverCell = await updateCellMutationRef.current.mutateAsync({
          tableId,
          sheetId,
          cellId: actionableCellId,
          display_value: nextDisplayValue,
          value: nextValue,
        });
        sheetDataStore.reconcileCell(serverCell);
      } catch (error) {
        if (snapshot) {
          sheetDataStore.restoreCellSnapshot(snapshot);
        }
        displayErrorToast("Failed to save human input.");
        throw error;
      }
    },
    [
      actionableCellId,
      canEditHumanInput,
      humanInputConfig,
      sheetDataStore,
      sheetId,
      tableId,
      updateCellMutationRef,
    ],
  );

  const effectiveColumnType = backingColumn?.type ?? columnType;
  const anchorImportType = getAnchorImportType(effectiveColumnType);
  const canStartCellImport =
    !isReadOnly &&
    !isHydrating &&
    !isPending &&
    classified.type === CellContentType.EMPTY &&
    parentColumnId === undefined &&
    metricKey === undefined &&
    actionableCellId !== null &&
    baseColumnId !== undefined &&
    openAnchorCellImport !== undefined;
  const canImportAnchor = canStartCellImport && anchorImportType !== null && !isSystemManagedField;
  const onImportAnchor = useCallback(() => {
    if (
      !canImportAnchor ||
      anchorImportType === null ||
      actionableCellId === null ||
      baseColumnId === undefined ||
      openAnchorCellImport === undefined
    ) {
      return;
    }
    openAnchorCellImport({
      cellId: actionableCellId,
      columnId: baseColumnId,
      type: anchorImportType,
    });
  }, [actionableCellId, anchorImportType, baseColumnId, canImportAnchor, openAnchorCellImport]);
  const canRunCell =
    !isReadOnly &&
    !isHydrating &&
    hasCellActionContext &&
    recalculateCellMutationRef !== undefined &&
    recalculateColumnMutationRef !== undefined &&
    isComputedColumn &&
    isRunnableColumnType(effectiveColumnType) &&
    rowIndex !== undefined &&
    baseColumnId !== undefined &&
    Boolean(tableId && sheetId) &&
    (isCompositionColumnCell ? true : actionableCellId !== null);

  const onCopyCell = useCallback(async () => {
    let valueToCopy = rawStoredValue ?? cellValue;
    let displayValueToCopy =
      backingCell?.display_value ?? (typeof cellValue === "string" ? cellValue : null);

    if (isTruncatedValueStub(rawStoredValue) || backingCell?.display_value_truncated === true) {
      const fullCellId =
        actionableCellId ?? (isCompositionColumnCell ? (backingCell?.id ?? null) : null);
      const virtualCell =
        isCompositionColumnCell && baseColumnId !== undefined && rowIndex !== undefined
          ? { columnId: baseColumnId, rowIndex }
          : undefined;
      if (!queryClient || wid === undefined || !tableId || !sheetId || !fullCellId) {
        displayErrorToast("Couldn’t load the full cell value to copy.");
        return;
      }

      try {
        const fullCell = await queryClient.fetchQuery({
          queryKey: getSmartTableCellQueryKey(wid, tableId, sheetId, {
            cellId: fullCellId,
            virtualCell,
          }),
          queryFn: () =>
            fetchSmartTableCell({
              wid,
              tableId,
              sheetId,
              cellId: fullCellId,
              userToken,
              virtualCell,
            }),
        });
        valueToCopy = fullCell.value ?? fullCell.display_value ?? cellValue;
        displayValueToCopy = fullCell.display_value;
      } catch {
        displayErrorToast("Couldn’t load the full cell value to copy.");
        return;
      }
    }

    const textToCopy = getSmartTableCellCopyText(valueToCopy, displayValueToCopy, {
      preferRawValue: isReadonlyAnchorColumnType(effectiveColumnType) && !metricKey,
    });
    if (textToCopy) {
      await navigator.clipboard.writeText(textToCopy);
      displayToast("Copied to clipboard", ToastType.success);
    }
  }, [
    actionableCellId,
    backingCell?.display_value,
    backingCell?.display_value_truncated,
    backingCell?.id,
    baseColumnId,
    cellValue,
    isCompositionColumnCell,
    effectiveColumnType,
    metricKey,
    queryClient,
    rawStoredValue,
    rowIndex,
    sheetId,
    tableId,
    userToken,
    wid,
  ]);

  const onRunCell = useCallback(() => {
    if (
      !canRunCell ||
      !sheetDataStore ||
      !recalculateCellMutationRef ||
      !recalculateColumnMutationRef ||
      !queryClient ||
      wid === undefined
    ) {
      return;
    }

    if (isCompositionColumnCell) {
      if (!tableId || !sheetId || baseColumnId === undefined || rowIndex === undefined) {
        return;
      }

      const localCellId = backingCell?.id;
      const snapshot = localCellId ? sheetDataStore.getCellSnapshot(localCellId) : null;
      if (localCellId && snapshot) {
        preRunCompositionSnapshotsRef.current.set(localCellId, snapshot);
        sheetDataStore.updateCellStatus(localCellId, CellStatus.QUEUED);
      }

      recalculateColumnMutationRef.current.mutate(
        {
          tableId,
          sheetId,
          columnId: baseColumnId,
          row_start: rowIndex,
          row_end: rowIndex + 1,
          force_recompute: false,
        },
        {
          onSuccess: raw => {
            const response = raw as { cell_count?: number; target_execution_id?: string };
            if (snapshot) {
              sheetDataStore.bindCellSnapshotsToExecution([snapshot], response.target_execution_id);
            }
            if (snapshot && typeof response.cell_count === "number" && response.cell_count === 0) {
              preRunCompositionSnapshotsRef.current.delete(snapshot.cellId);
              sheetDataStore.restoreCellSnapshot(snapshot);
            }

            void queryClient.invalidateQueries({
              queryKey: ["smart-tables", wid, tableId, "bootstrap"],
            });
          },
          onError: () => {
            if (snapshot) {
              preRunCompositionSnapshotsRef.current.delete(snapshot.cellId);
              sheetDataStore.restoreCellSnapshot(snapshot);
            }
            displayErrorToast("Failed to recalculate cell.");
          },
        },
      );
      return;
    }

    if (!actionableCellId || !tableId || !sheetId) {
      return;
    }

    const selectedCellIds = getRunnableSelectedCellIds({ api, sheetDataStore });
    if (selectedCellIds.length > 1 && selectedCellIds.includes(actionableCellId)) {
      if (!batchRecalculateCellsMutationRef) {
        return;
      }

      const uniqueCellIds = Array.from(new Set(selectedCellIds));
      const snapshots = sheetDataStore.markCellsQueued(uniqueCellIds);

      batchRecalculateCellsMutationRef.current.mutate(
        {
          tableId,
          sheetId,
          cell_ids: uniqueCellIds,
        },
        {
          onSuccess: raw => {
            const response = raw as { cell_count?: number; target_execution_id?: string };
            sheetDataStore.bindCellSnapshotsToExecution(snapshots, response.target_execution_id);
            if (typeof response.cell_count === "number" && response.cell_count === 0) {
              sheetDataStore.restoreCellSnapshots(snapshots);
            }
          },
          onError: () => {
            sheetDataStore.restoreCellSnapshots(snapshots);
            displayErrorToast("Failed to run selected cells.");
          },
        },
      );
      return;
    }

    const localCellId = backingCell?.id ?? actionableCellId;
    const snapshots = sheetDataStore.markCellsQueued([localCellId]);

    recalculateCellMutationRef.current.mutate(
      {
        tableId,
        sheetId,
        cellId: actionableCellId,
      },
      {
        onSuccess: raw => {
          const response = raw as { cell_count?: number; target_execution_id?: string };
          sheetDataStore.bindCellSnapshotsToExecution(snapshots, response.target_execution_id);
          if (typeof response.cell_count === "number" && response.cell_count === 0) {
            sheetDataStore.restoreCellSnapshots(snapshots);
          }
        },
        onError: () => {
          sheetDataStore.restoreCellSnapshots(snapshots);
          displayErrorToast("Failed to recalculate cell.");
        },
      },
    );
  }, [
    actionableCellId,
    api,
    backingCell?.id,
    baseColumnId,
    batchRecalculateCellsMutationRef,
    canRunCell,
    isCompositionColumnCell,
    queryClient,
    recalculateCellMutationRef,
    recalculateColumnMutationRef,
    rowIndex,
    sheetDataStore,
    sheetId,
    tableId,
    wid,
  ]);

  const canStopCell =
    !isReadOnly &&
    !isHydrating &&
    hasCellActionContext &&
    cancelCellMutationRef !== undefined &&
    cancelRowsMutationRef !== undefined &&
    isComputedColumn &&
    isRunnableColumnType(effectiveColumnType) &&
    rowIndex !== undefined &&
    baseColumnId !== undefined &&
    Boolean(tableId && sheetId) &&
    sheetDataStore?.isCellExecutionPending(rowIndex, baseColumnId) === true &&
    (isCompositionColumnCell ? compositionRowCancelTargets.length > 0 : actionableCellId !== null);

  const onStopCell = useCallback(() => {
    if (
      !canStopCell ||
      !cancelCellMutationRef ||
      !cancelRowsMutationRef ||
      !queryClient ||
      wid === undefined
    ) {
      return;
    }

    if (isCompositionColumnCell) {
      if (rowIndex === undefined || compositionRowCancelTargets.length === 0) {
        return;
      }

      const localCellId = backingCell?.id;
      const preRunSnapshot = localCellId
        ? preRunCompositionSnapshotsRef.current.get(localCellId)
        : undefined;

      compositionRowCancelTargets.forEach(target => {
        cancelRowsMutationRef.current.mutate(
          {
            tableId: target.tableId,
            sheetId: target.sheetId,
            row_indices: [rowIndex],
          },
          {
            onSuccess: () => {
              if (preRunSnapshot) {
                sheetDataStore?.restoreCellSnapshot(preRunSnapshot);
                preRunCompositionSnapshotsRef.current.delete(preRunSnapshot.cellId);
              }
              void queryClient.invalidateQueries({
                queryKey: ["smart-tables", wid, tableId, "bootstrap"],
              });
            },
            onError: () => displayErrorToast("Failed to stop cell."),
          },
        );
      });
      return;
    }

    if (!actionableCellId || !tableId || !sheetId) {
      return;
    }

    cancelCellMutationRef.current.mutate(
      {
        tableId,
        sheetId,
        cellId: actionableCellId,
      },
      {
        onError: () => displayErrorToast("Failed to stop cell."),
      },
    );
  }, [
    actionableCellId,
    backingCell?.id,
    canStopCell,
    cancelCellMutationRef,
    cancelRowsMutationRef,
    compositionRowCancelTargets,
    isCompositionColumnCell,
    queryClient,
    rowIndex,
    sheetDataStore,
    sheetId,
    tableId,
    wid,
  ]);

  const onUploadFiles = useCallback(
    async (files: File[]) => {
      if (
        !canUpload ||
        rowIndex === undefined ||
        baseColumnId === undefined ||
        !tableId ||
        !sheetId ||
        !sheetDataStore ||
        !updateCellMutationRef
      ) {
        return;
      }

      const targetCell = sheetDataStore.rows.get(rowIndex)?.get(baseColumnId);
      if (!targetCell) {
        return;
      }

      try {
        const uploadedMedia = await Promise.all(files.map(fileToOutputMediaItem));
        const nextValue = mergeUploadedMediaIntoCellValue(targetCell.value, uploadedMedia);

        const isMediaArray = Array.isArray(nextValue);
        const nextDisplayValue = isMediaArray
          ? `${nextValue.length} file${nextValue.length === 1 ? "" : "s"}`
          : "multi_content";
        const nextJsonValue = nextValue as unknown as JsonValue;

        const snapshot = sheetDataStore.getCellSnapshot(targetCell.id);
        sheetDataStore.updateCell(targetCell.id, nextDisplayValue, nextJsonValue);

        updateCellMutationRef.current.mutate(
          {
            tableId,
            sheetId,
            cellId: targetCell.id,
            display_value: nextDisplayValue,
            value: nextJsonValue,
          },
          {
            onSuccess: serverCell => sheetDataStore.reconcileCell(serverCell),
            onError: () => {
              sheetDataStore.restoreCellSnapshot(snapshot);
              displayErrorToast("Failed to upload files to cell.");
            },
          },
        );
      } catch {
        displayErrorToast("Failed to read one or more files.");
      }
    },
    [baseColumnId, canUpload, rowIndex, sheetDataStore, sheetId, tableId, updateCellMutationRef],
  );

  const leadingIcon = useMemo(
    () =>
      getAnchorCellLeadingIcon({
        columnType: effectiveColumnType,
        metricKey,
        hasContent,
        rawValue: rawStoredValue,
      }),
    [effectiveColumnType, hasContent, metricKey, rawStoredValue],
  );

  const previewProps = useMemo(
    () => ({
      eGridCell,
      assertionModel,
      classified,
      displayText,
      hasContent,

      isComputedDisplay,
      isError,
      isJson,
      isLLMAssertion,
      isMedia,
      isPending,
      isHydrating,
      isVisuallyStale,
      isCompare,
      isHumanInput: isHumanColumn,
      humanInputConfig,
      humanInputValue,
      canEditHumanInput,
      onSaveHumanInput: canEditHumanInput ? onSaveHumanInput : undefined,
      compareDiff,
      mediaPayload,
      multiContentPayload:
        classified.type === CellContentType.MULTI_CONTENT
          ? classified.multiContentPayload
          : undefined,
      linkPayload,
      onOpenDialog: canOpenDialog ? onOpenDialog : undefined,
      openRequestUrl,
      openRequestLabel,
      executionMetricsOverlay,
      canUpload,
      onUploadFiles: canUpload ? onUploadFiles : undefined,
      onCopyCell,
      onImportAnchor: canImportAnchor ? onImportAnchor : undefined,
      importAnchorLabel: getAnchorImportLabel(anchorImportType),
      onRunCell: canRunCell && !canStopCell ? onRunCell : undefined,
      onStopCell: canStopCell ? onStopCell : undefined,
      richHtml,
      smartComputedTextClass,
      computedBooleanValue,
      status,
      textClass,
      tooltipText,
      globalSearchQuery,
      userFacingError,
      leadingIcon,
    }),
    [
      eGridCell,
      assertionModel,
      classified,
      displayText,
      hasContent,

      isComputedDisplay,
      isError,
      isJson,
      isLLMAssertion,
      isMedia,
      isPending,
      isHydrating,
      isVisuallyStale,
      isCompare,
      isHumanColumn,
      humanInputConfig,
      humanInputValue,
      canEditHumanInput,
      onSaveHumanInput,
      compareDiff,
      mediaPayload,
      linkPayload,
      canOpenDialog,
      onOpenDialog,
      openRequestUrl,
      openRequestLabel,
      executionMetricsOverlay,
      canUpload,
      onUploadFiles,
      onCopyCell,
      onImportAnchor,
      canImportAnchor,
      anchorImportType,
      onRunCell,
      canRunCell,
      onStopCell,
      canStopCell,
      richHtml,
      smartComputedTextClass,
      computedBooleanValue,
      status,
      textClass,
      tooltipText,
      globalSearchQuery,
      userFacingError,
      leadingIcon,
    ],
  );

  return {
    eGridCell,
    hasContent,
    isPending,
    isVisuallyStale,
    isCompare,
    isLLMAssertion,
    isHumanColumn,
    computedBooleanValue,
    statusFilterHighlightTone,
    previewProps,
  };
}
